import React, { useState, useEffect } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { useForm, Controller } from 'react-hook-form';
import config from '../auth/config';
import { MapContainer, TileLayer, Marker, Tooltip, Popup, useMap } from 'react-leaflet';
import './RampExposureWizard.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import FeederCombobox from './FeederCombobox';
import {
  DEFAULT_FEEDER_ID,
  feedersById,
  substations,
  substationsById,
  feedersBySubstationId,
} from '../data/feederMap';

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
});

const DEFAULT_MAP_CENTER = [29.5, -97.7];

const defaultSubstation = substationsById[feedersById[DEFAULT_FEEDER_ID]?.substationId];

const DEFAULT_FEEDER_CENTER = defaultSubstation
  ? [defaultSubstation.coordinates.lat, defaultSubstation.coordinates.lng]
  : DEFAULT_MAP_CENTER;

const defaultSubstationIcon = L.divIcon({
  className: 'feeder-marker feeder-marker-default',
  html: '',
  iconSize: [12, 12],
  iconAnchor: [5, 5],
});

const selectedSubstationIcon = L.divIcon({
  className: 'feeder-marker feeder-marker-selected',
  html: '',
  iconSize: [12, 12],
  iconAnchor: [5, 5],
});

const FORECAST_VIEW_MAP = { Q50: 0.50, Q70: 0.70, Q90: 0.90, Q95: 0.95 };

const defaultValues = {
  projectName: '',
  interconnectionDate: '',
  feeder: DEFAULT_FEEDER_ID,
  technology: 'solar',
  capacityKw: '',
  batteryCapacity: '',
  simulationStart: '',
  simulationEnd: '',
  rampResolution: '1h',
  dailyForecastViews: ['Q50'],
};

const steps = [
  {
    title: 'Project Info',
    description: 'Name the project and choose the feeder',
    fields: ['projectName', 'interconnectionDate', 'feeder'],
  },
  {
    title: 'Technology and Sizing',
    description: 'Specify the generation technology and size',
    fields: ['technology', 'capacityKw', 'batteryCapacity'],
  },
  {
    title: 'Analysis',
    description: 'Choose the simulation window and forecast outputs',
    fields: ['simulationStart', 'simulationEnd', 'rampResolution', 'dailyForecastViews'],
  },
  {
    title: 'Review & Submit',
    description: 'Confirm the analysis setup',
    fields: [],
  },
];

const getAccessToken = () => {
  return new Promise((resolve, reject) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      reject(new Error('No user logged in'));
      return;
    }

    currentUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      resolve(session.getAccessToken().getJwtToken());
    });
  });
};

const formatTechnology = (technology) => {
  if (technology === 'solar') {
    return 'Solar';
  }

  if (technology === 'bess') {
    return 'BESS';
  }

  return technology || 'Not specified';
};

const formatRampResolution = (rampResolution) => {
  if (rampResolution === '1h') {
    return '1 hour';
  }

  if (rampResolution === '15m') {
    return '15 minutes';
  }

  return rampResolution || 'Not specified';
};

const getTodayIso = () => new Date().toISOString().slice(0, 10);

const getTomorrowIso = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().slice(0, 10);
};

const isFutureDate = (value) => Boolean(value) && value > getTodayIso();

function MapZoomTransition({ position }) {
  const map = useMap();

  useEffect(() => {
    if (!position) {
      return;
    }
    map.whenReady(() => {
      map.flyTo(position, 13, {
        duration: 0.5,
      });
    });
  }, [map, position]);

  return null;
}

function RampExposureWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [isSubmittingRampExposure, setIsSubmittingRampExposure] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [submitResponse, setSubmitResponse] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues,
    shouldUnregister: false,
  });

  const values = watch();
  const minFutureDate = getTomorrowIso();
  const activeStep = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isReviewStep = currentStep === steps.length - 1;
  const selectedFeeder = values.feeder ? feedersById[values.feeder] : null;
  const selectedSubstation = selectedFeeder
    ? substationsById[selectedFeeder.substationId]
    : null;
  const mapCenter = selectedSubstation
    ? [selectedSubstation.coordinates.lat, selectedSubstation.coordinates.lng]
    : DEFAULT_FEEDER_CENTER;

  const persistFormValues = () => {
    reset(getValues(), {
      keepErrors: true,
      keepDirty: true,
      keepTouched: true,
    });
  };

  const goToStep = async (stepIndex) => {
    if (stepIndex > furthestStep && stepIndex !== currentStep + 1) {
      return;
    }

    if (stepIndex <= currentStep) {
      persistFormValues();
      setCurrentStep(stepIndex);
      setSubmitError('');
      setHasAttemptedSubmit(false);
      return;
    }

    const fieldsToValidate = steps[currentStep].fields;
    const canAdvance = fieldsToValidate.length === 0 || await trigger(fieldsToValidate);

    if (canAdvance) {
      persistFormValues();
      setCurrentStep(stepIndex);
      setFurthestStep((step) => Math.max(step, stepIndex));
      setSubmitError('');
      setHasAttemptedSubmit(false);
    }
  };

  const goToNextStep = async () => {
    await goToStep(Math.min(currentStep + 1, steps.length - 1));
  };

  const goToPreviousStep = () => {
    persistFormValues();
    setCurrentStep((step) => Math.max(step - 1, 0));
    setSubmitError('');
    setHasAttemptedSubmit(false);
  };

  const buildPayload = (formValues) => {
    const payload = {
      projectName: formValues.projectName,
      feeder: formValues.feeder,
      start: formValues.simulationStart.slice(0, 10),
      end: formValues.simulationEnd.slice(0, 10),
      capacities: [0, Number(formValues.capacityKw)],
      dailyForecastViews: formValues.dailyForecastViews.map(v => FORECAST_VIEW_MAP[v]),
    };
    const battery = parseInt(formValues.batteryCapacity, 10);
    if (formValues.batteryCapacity !== '' && Number.isFinite(battery) && battery > 0) {
      payload.batteryCapacity = battery;
    }
    return payload;
  };

  const onSubmit = async (formValues) => {
    setHasAttemptedSubmit(true);
    setIsSubmittingRampExposure(true);
    setSubmitError('');

    try {
      const accessToken = await getAccessToken();
      const response = await fetch('/api/ramp-exposure', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(buildPayload(formValues)),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('API returned non-JSON response. Check that your dev proxy is configured.');
      }

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }

        throw new Error(responseBody.error || 'Failed to submit ramp exposure analysis.');
      }

      setSubmitResponse(responseBody);
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmittingRampExposure(false);
    }
  };

  const runNewAnalysis = () => {
    reset(defaultValues);
    setCurrentStep(0);
    setFurthestStep(0);
    setSubmitError('');
    setHasAttemptedSubmit(false);
    setSubmitResponse(null);
    setIsSubmittingRampExposure(false);
  };

  const renderFieldError = (fieldName) => {
    if (!errors[fieldName]) {
      return null;
    }

    return <span className="ramp-field-error">{errors[fieldName].message || 'Required'}</span>;
  };

  const renderProjectInfoStep = () => (
    <div className="ramp-step-fields">
      <label className="ramp-field">
        <span>Project Name</span>
        <input
          type="text"
          required
          {...register('projectName', { required: true })}
        />
        {renderFieldError('projectName')}
      </label>

      <label className="ramp-field">
        <span>Interconnection Date</span>
        <input
          type="date"
          min={minFutureDate}
          required
          {...register('interconnectionDate', {
            required: 'Required',
            validate: {
              future: (value) => isFutureDate(value) || 'Must be in the future',
            },
          })}
        />
        {renderFieldError('interconnectionDate')}
      </label>

      <div className="ramp-field">
        <span>Feeder</span>
        <Controller
          name="feeder"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FeederCombobox
              value={field.value}
              onChange={field.onChange}
              name={field.name}
              ariaInvalid={Boolean(errors.feeder)}
            />
          )}
        />
        {renderFieldError('feeder')}
      </div>

      <div className="ramp-field">
        <div className="ramp-map">
          <MapContainer
            center={mapCenter}
            zoom={11}
            scrollWheelZoom={false}
            style={{
              height: '100%',
              width: '100%',
            }}
          >

            <MapZoomTransition position={mapCenter} />
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {substations.map((substation) => {
              const position = [substation.coordinates.lat, substation.coordinates.lng];
              const isSelected = substation.id === selectedSubstation?.id;
              const substationFeeders = feedersBySubstationId[substation.id] || [];

              return (
                <Marker
                  key={substation.id}
                  position={position}
                  icon={isSelected ? selectedSubstationIcon : defaultSubstationIcon}
                >
                  <Tooltip
                    direction="top"
                    offset={[0, -10]}
                    className={isSelected ? 'feeder-tooltip-selected' : 'feeder-tooltip'}
                  >
                    {substation.name}
                  </Tooltip>
                  <Popup>
                    <div className="substation-popup">
                      <p className="substation-popup-title">{substation.name}</p>
                      {substationFeeders.length === 0 ? (
                        <p className="substation-popup-empty">No feeders available.</p>
                      ) : (
                        <ul className="feeder-popup-list">
                          {substationFeeders.map((feeder) => {
                            const isFeederSelected = feeder.id === values.feeder;
                            return (
                              <li key={feeder.id}>
                                <button
                                  type="button"
                                  className={`feeder-popup-item ${isFeederSelected ? 'is-selected' : ''}`}
                                  onClick={() => {
                                    setValue('feeder', feeder.id, {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    });
                                  }}
                                >
                                  {feeder.id}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

          </MapContainer>
        </div>
      </div>

    </div>
  );

  const renderTechnologyStep = () => (
    <div className="ramp-step-fields">
      <label className="ramp-field">
        <span>Technology</span>
        <select required {...register('technology', { required: true })}>
          <option value="solar">Solar</option>
          <option value="bess" disabled>BESS</option>
        </select>
        {renderFieldError('technology')}
      </label>

      <label className="ramp-field">
        <span>Project Size (kW)</span>
        <input
          type="number"
          step="1"
          required
          {...register('capacityKw', { required: true })}
        />
        {renderFieldError('capacityKw')}
      </label>

      <label className="ramp-field">
        <span>Battery Size (kWh) <small>— optional</small></span>
        <input
          type="number"
          step="1"
          min="0"
          {...register('batteryCapacity', {
            validate: (value) =>
              value === '' ||
              (Number.isInteger(Number(value)) && Number(value) >= 0) ||
              'Must be a non-negative whole number',
          })}
        />
        {renderFieldError('batteryCapacity')}
      </label>
    </div>
  );

  const renderAnalysisStep = () => (
    <div className="ramp-step-fields">
      <label className="ramp-field">
        <span>Simulation Start</span>
        <input
          type="date"
          min={minFutureDate}
          required
          {...register('simulationStart', {
            required: 'Required',
            validate: {
              future: (value) => isFutureDate(value) || 'Must be in the future',
            },
            onChange: () => {
              if (getValues('simulationEnd')) {
                void trigger('simulationEnd');
              }
            },
          })}
        />
        {renderFieldError('simulationStart')}
      </label>

      <label className="ramp-field">
        <span>Simulation End</span>
        <input
          type="date"
          min={minFutureDate}
          required
          {...register('simulationEnd', {
            required: 'Required',
            validate: {
              future: (value) => isFutureDate(value) || 'Must be in the future',
              afterStart: (value) => value > getValues('simulationStart') || 'Must be after Simulation Start',
            },
          })}
        />
        {renderFieldError('simulationEnd')}
      </label>

      <label className="ramp-field">
        <span>Ramp Analysis Resolution</span>
        <select required {...register('rampResolution', { required: true })}>
          <option value="1h">1 hour</option>
          <option value="15m" disabled>15 minutes</option>
        </select>
        {renderFieldError('rampResolution')}
      </label>

      <fieldset className="ramp-checkbox-group">
        <legend>Sample Daily Ramp Quantiles</legend>
        <label>
          <input type="checkbox" value="Q50" {...register('dailyForecastViews')} />
          Q50
        </label>
        <label>
          <input type="checkbox" value="Q70" {...register('dailyForecastViews')} />
          Q70
        </label>
        <label>
          <input type="checkbox" value="Q90" {...register('dailyForecastViews')} />
          Q90
        </label>
        <label>
          <input type="checkbox" value="Q95" {...register('dailyForecastViews')} />
          Q95
        </label>
      </fieldset>
    </div>
  );

  const renderReviewStep = () => (
    <dl className="review-grid">
      <dt>Project Name</dt>
      <dd>{values.projectName || 'Not specified'}</dd>

      <dt>Interconnection Date</dt>
      <dd>{values.interconnectionDate || 'Not specified'}</dd>

      <dt>Feeder</dt>
      <dd>{values.feeder || 'Not specified'}</dd>

      <dt>Substation</dt>
      <dd>{selectedSubstation?.name || 'Not specified'}</dd>

      <dt>Technology</dt>
      <dd>{formatTechnology(values.technology)}</dd>

      <dt>Capacity</dt>
      <dd>{values.capacityKw ? `${values.capacityKw} kW` : 'Not specified'}</dd>

      <dt>Battery Capacity</dt>
      <dd>{values.batteryCapacity ? `${values.batteryCapacity} kWh` : 'Not specified'}</dd>

      <dt>Simulation Start</dt>
      <dd>{values.simulationStart || 'Not specified'}</dd>

      <dt>Simulation End</dt>
      <dd>{values.simulationEnd || 'Not specified'}</dd>

      <dt>Ramp Resolution</dt>
      <dd>{formatRampResolution(values.rampResolution)}</dd>

      <dt>Forecast Views</dt>
      <dd>{values.dailyForecastViews?.length ? values.dailyForecastViews.join(', ') : 'None selected'}</dd>
    </dl>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderProjectInfoStep();
      case 1:
        return renderTechnologyStep();
      case 2:
        return renderAnalysisStep();
      case 3:
        return renderReviewStep();
      default:
        return null;
    }
  };

  if (submitResponse) {
    return (
      <section className="ramp-wizard ramp-wizard-success" aria-live="polite">
        <div className="ramp-success-card">
          <p className="ramp-eyebrow">Submitted</p>
          <h3>Ramp exposure analysis queued</h3>
          <p>
            Your RES analysis has been submitted successfully.
            {submitResponse.jobId ? ` Job ID: ${submitResponse.jobId}.` : ''}
          </p>
          <button type="button" className="ramp-primary-button" onClick={runNewAnalysis}>
            Run New Analysis
          </button>
        </div>
      </section>
    );
  }

  return (
    <form
      className="ramp-wizard"
      noValidate
      onSubmit={(event) => event.preventDefault()}
    >
      <aside className="ramp-progress" aria-label="Ramp exposure wizard progress">
        <p className="ramp-eyebrow">RES Intake Form</p>
        <ol>
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isComplete = index < furthestStep;
            const isDisabled = index > furthestStep && index !== currentStep + 1;

            return (
              <li key={step.title}>
                <button
                  type="button"
                  className={`ramp-step-nav ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`}
                  onClick={() => goToStep(index)}
                  aria-current={isActive ? 'step' : undefined}
                  disabled={isDisabled}
                >
                  <span className="ramp-step-index">{index + 1}</span>
                  <span>
                    <strong>{step.title}</strong>
                    <small>{step.description}</small>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </aside>

      <section className="ramp-step-panel">
        <div className="ramp-step-header">
          <p className="ramp-eyebrow">Step {currentStep + 1} of {steps.length}</p>
          <h3>{activeStep.title}</h3>
          <p>{activeStep.description}</p>
        </div>

        <div className="ramp-step-body">
          {renderStepContent()}
        </div>

        {isReviewStep && hasAttemptedSubmit && submitError && (
          <div className="ramp-submit-error" role="alert">
            {submitError}
          </div>
        )}

        <div className="ramp-actions">
          <button
            type="button"
            className="ramp-secondary-button"
            onClick={goToPreviousStep}
            disabled={isFirstStep || isSubmittingRampExposure}
          >
            Back
          </button>

          {isReviewStep ? (
            <button
              type="button"
              className="ramp-primary-button"
              disabled={isSubmittingRampExposure}
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmittingRampExposure ? 'Submitting...' : 'Submit to Run RES Analysis'}
            </button>
          ) : (
            <button
              type="button"
              className="ramp-primary-button"
              onClick={goToNextStep}
              disabled={isSubmittingRampExposure}
            >
              Next
            </button>
          )}
        </div>
      </section>
    </form>
  );
}

export default RampExposureWizard;